/**
 * W1-11b (app-shell-ui): fuzzy matching for the command palette (DESIGN.md §4.19): titles, aliases (RPE and Chinese
 * community terms: "stick", "cut", "MirrorY", 粘合), categories and ids. Case- and accent-insensitive; every query
 * word must match one of the fields (as a prefix, a substring, word initials or a subsequence); better matches score
 * higher. CJK text matches by substring (no word boundaries needed).
 */

/** Lower-case, accents removed, full-width forms folded. */
export function normalizeText(s: string): string {
  return s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Word start positions of a normalised text (after separators and camelCase humps of the original). */
function wordStarts(original: string, norm: string): Set<number> {
  const starts = new Set<number>([0]);
  for (let i = 1; i < norm.length; i++) {
    const prev = norm[i - 1] ?? '';
    const cur = original[i] ?? '';
    const prevOrig = original[i - 1] ?? '';
    if (/[^\p{L}\p{N}]/u.test(prev)) starts.add(i);
    else if (/\p{Lu}/u.test(cur) && /\p{Ll}/u.test(prevOrig)) starts.add(i);
    else if (/\p{N}/u.test(cur) !== /\p{N}/u.test(prevOrig)) starts.add(i);
  }
  return starts;
}

/** Score of one query word against one text (null = no match). */
export function wordScore(word: string, text: string): number | null {
  const q = normalizeText(word);
  if (q === '') return 0;
  const t = normalizeText(text);
  if (t === '') return null;
  if (t === q) return 1000;
  if (t.startsWith(q)) return 900 - Math.min(100, t.length - q.length);
  const starts = wordStarts(text.length === t.length ? text : t, t);
  // Substring at a word start ("snap" in "Set beat snap"), then anywhere.
  let best: number | null = null;
  for (let i = t.indexOf(q); i >= 0; i = t.indexOf(q, i + 1)) {
    const s = starts.has(i) ? 700 - i : 450 - i;
    best = best === null ? s : Math.max(best, s);
  }
  if (best !== null) return best;
  // Word initials ("bs" -> "beat snap").
  const initials = [...starts]
    .sort((a, b) => a - b)
    .map((i) => t[i] ?? '')
    .join('');
  if (initials.startsWith(q)) return 380;
  // Subsequence with gap penalty. A scattered match ("iscover" inside a long unrelated title) is no match at all.
  let ti = 0;
  let gaps = 0;
  let last = -1;
  let first = -1;
  for (const ch of q) {
    const found = t.indexOf(ch, ti);
    if (found < 0) return null;
    if (first < 0) first = found;
    if (last >= 0) gaps += found - last - 1;
    last = found;
    ti = found + 1;
  }
  const span = last - first + 1;
  if (span > q.length * 2 + 3) return null;
  return Math.max(1, 200 - gaps * 8 - (starts.has(t.indexOf(q[0] ?? '')) ? 0 : 20));
}

export interface SearchFields {
  readonly title: string;
  readonly aliases?: readonly string[];
  readonly category?: string;
  readonly id?: string;
  readonly detail?: string;
}

const WEIGHTS = { title: 1, aliases: 0.9, id: 0.6, category: 0.5, detail: 0.4 } as const;

/** Score of a query against a candidate; null when some query word matches no field. */
export function searchScore(query: string, fields: SearchFields): number | null {
  const words = query
    .trim()
    .split(/\s+/)
    .filter((w) => w !== '');
  if (words.length === 0) return 0;
  // The whole query as one phrase against the title first (multi-word titles typed in full).
  const phrase = words.length > 1 ? wordScore(words.join(' '), fields.title) : null;
  let total = 0;
  for (const w of words) {
    const candidates: Array<readonly [string | undefined, number]> = [
      [fields.title, WEIGHTS.title],
      ...(fields.aliases ?? []).map((a) => [a, WEIGHTS.aliases] as const),
      [fields.id, WEIGHTS.id],
      [fields.category, WEIGHTS.category],
      [fields.detail, WEIGHTS.detail],
    ];
    let best: number | null = null;
    for (const [text, weight] of candidates) {
      if (text === undefined) continue;
      const s = wordScore(w, text);
      if (s !== null) best = best === null ? s * weight : Math.max(best, s * weight);
    }
    if (best === null) return null;
    total += best;
  }
  return phrase === null ? total : Math.max(total, phrase * words.length);
}

/** Ranks candidates: matches only, best first; ties keep the input order (which already puts recents first). */
export function rank<T>(
  items: readonly T[],
  query: string,
  fields: (item: T) => SearchFields,
  bonus?: (item: T) => number,
): T[] {
  if (query.trim() === '') return [...items];
  const scored: Array<{ readonly item: T; readonly score: number; readonly i: number }> = [];
  items.forEach((item, i) => {
    const s = searchScore(query, fields(item));
    if (s !== null) scored.push({ item, score: s + (bonus?.(item) ?? 0), i });
  });
  scored.sort((a, b) => b.score - a.score || a.i - b.i);
  return scored.map((x) => x.item);
}

/**
 * Community vocabulary the commands' own aliases miss (UX-009): RPE's names ("event" for a keyframe segment,
 * "random" for the batch tool's seeded randomness) and the Chinese community terms for them. Keyed by command id;
 * joined with `CommandDef.aliases` by the palette. The isCover / 遮罩 vocabulary is answered by the help glossary
 * rows (`HELP_ALIASES`, there is no cover command yet).
 */
export const EXTRA_ALIASES: Readonly<Record<string, readonly string[]>> = {
  'keys.addAtPlayhead': ['event', 'add event', 'new event', '事件', '添加事件'],
  'keys.addHere': ['event', 'add event', '事件'],
  'keys.delete': ['delete event', 'remove event', '删除事件'],
  'segments.insert': ['event', 'insert event', '事件'],
  'segments.delete': ['delete event', 'remove event', '删除事件'],
  'segments.easing': ['event easing', 'ease', '缓动'],
  'motion.placeSegment': ['event', 'place event', '事件'],
  'motion.layers': ['event layer', '事件层级', 'layer'],
  // RPE's MultiNote / MultiEvent editor with its seeded randomness ("随机"), not the Transform dialog.
  'tools.distribute': ['rand', 'random', 'randomize', 'jitter', 'random values', 'seed', '随机', '随机数值'],
};

/**
 * Community vocabulary of the palette's help rows (UX-009), keyed by the provider's item id
 * (`help.glossary.<id>` / `help.topic.<id>`). The cover row carries the isCover / 遮罩 names of the glossary, so
 * "cover", "iscover" and "遮罩" all reach "Hide notes under the line" even though no command is named that yet.
 */
export const HELP_ALIASES: Readonly<Record<string, readonly string[]>> = {
  'help.glossary.cover': [
    'cover',
    'isCover',
    'iscover',
    '遮罩',
    'hide notes under the line',
    'hide notes',
    'mask',
  ],
};

/** Ends with an ellipsis already ("Stagger selection...", "Edit layer…"). */
export function endsWithEllipsis(title: string): boolean {
  return /(\.\.\.|\u2026)\s*$/.test(title);
}
